import { Graphics, Circle } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { Fader } from "./fader"

// Fade timing for showing/dismissing the wheel. ms-per-step × steps-to-cross ≈ ~100ms each way.
const WHEEL_FADE_INTERVAL_MS = 5
const WHEEL_FADE_STEP = 0.05

// Ember-themed look + geometry. Kept as module constants (not props) so the visual treatment stays
// consistent regardless of the per-state colors a caller passes in.
const WHEEL_RIM_COLOR = 0xf2a94e // warm amber rim + soft glow
const WHEEL_DISC_ALPHA = 0.85 // opacity of the dark "glass" disc (legible over the world)
const WHEEL_SLICE_IDLE_COLOR = 0x3a2c22 // resting slice fill (warm charcoal)
const WHEEL_SLICE_IDLE_ALPHA = 0.55
const WHEEL_GAP_ANGLE = 0.06 // radians of empty space on each side of a slice (purely visual)
const WHEEL_INNER_RATIO = 0.3 // donut-hole radius as a fraction of the wheel radius
const WHEEL_HUB_RATIO = 0.22 // center hub radius as a fraction of the wheel radius

export interface CommandWheelProps {
    radius?: number
    strokeWidth?: number
    backgroundColor?: number
    textColor?: number
    highlightColor?: number
    activeColor?: number
    onCommandSelect?: (command: string, npcType: string) => void
}

export type CommandWheelRole = "forager" | "defender"

// Per-role accent for the per-NPC (role-locked) wheel — matches ROLE_COLORS in ember_roster.ts,
// hand-synced to avoid an import cycle (command_wheel ← gui_manager → ember_roster). The global
// (broadcast) wheel keeps the neutral amber WHEEL_RIM_COLOR instead.
const ROLE_ACCENT: Record<CommandWheelRole, number> = {
    forager: 0x7be08a,
    defender: 0xff8a5c,
}

export interface NPCCommand {
    name: string
    displayName: string
}

export interface NPCType {
    name: string
    displayName: string
    emoji: string
    commands: NPCCommand[]
}

export class CommandWheel extends Graphics {
    private radius: number
    private strokeWidth: number
    private backgroundColor: number
    private textColor: number
    private highlightColor: number
    private activeColor: number
    private onCommandSelect?: (command: string, npcType: string) => void

    private backgroundCircle: Graphics
    private commandSlices: Graphics[] = []
    private commandTexts: Graphics[] = []
    private centerBackground: Graphics
    private centerEmoji: any
    // Switchability affordances shown ONLY on the global (broadcast) hub: a ↻ hint and one page dot
    // per role. Tracked so setupCenterDisplay can tear them down instead of leaking children.
    private switchGlyph?: any
    private pageDots?: Graphics
    private currentNPCIndex: number = 0
    private activeCommandIndex: number = -1
    private hoveredCommandIndex: number = -1
    // When set, the wheel commands one specific NPC (opened from that entity's command button)
    // rather than broadcasting to its whole role. Cleared for the global Control-key wheel.
    private targetEntityId?: string
    // While targeting a single NPC the role page is locked (center switcher disabled).
    private roleLocked: boolean = false

    // Fade state. `opened` is the logical open/closed flag (flips the instant a fade starts) so
    // callers see the wheel as "closed" the moment it begins dismissing, while the graphic keeps
    // rendering until its fade-out completes. Only one fader runs at a time.
    private opened: boolean = false
    private fadeInFader: Fader | null = null
    private fadeOutFader: Fader | null = null

    // Role glyphs match ROLE_EMOJI in ember_roster.ts (kept in sync by hand to avoid an import
    // cycle: command_wheel ← gui_manager → ember_roster).
    private npcTypes: NPCType[] = [
        {
            name: "forager",
            displayName: "Forager",
            emoji: "🪓",
            commands: [
                { name: "forager:forage", displayName: "Forage Wood" },
                { name: "forager:feed_fire", displayName: "Feed Fire" },
                { name: "forager:head_back", displayName: "Head Back" },
                { name: "forager:follow", displayName: "Follow Me" },
            ],
        },
        {
            name: "defender",
            displayName: "Defender",
            emoji: "🛡️",
            commands: [
                { name: "defender:defend", displayName: "Defend Camp" },
                { name: "defender:defender:engage", displayName: "Engage" },
                { name: "defender:hold", displayName: "Hold Position" },
                { name: "defender:follow", displayName: "Follow Me" },
            ],
        },
    ]

    constructor(props: CommandWheelProps = {}) {
        super()

        this.radius = props.radius || 80
        this.strokeWidth = props.strokeWidth || 3
        this.backgroundColor = props.backgroundColor || 0x333333
        this.textColor = props.textColor || 0xffffff
        this.highlightColor = props.highlightColor || 0x4caf50
        this.activeColor = props.activeColor || 0x2196f3
        this.onCommandSelect = props.onCommandSelect

        this.setupWheel()
        this.updateDisplay()
    }

    private wrapText(text: string, maxWidth: number): string[] {
        const words = text.split(" ")
        const lines: string[] = []
        let currentLine = ""

        for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word
            // More accurate estimation: 8 pixels per character for 11px bold font
            const estimatedWidth = testLine.length * 8

            if (estimatedWidth <= maxWidth) {
                currentLine = testLine
            } else {
                if (currentLine) {
                    lines.push(currentLine)
                    currentLine = word
                } else {
                    // If even a single word is too long, split it intelligently
                    if (word.length * 8 > maxWidth) {
                        // Try to split at natural break points (hyphens, etc.)
                        const hyphenIndex = word.indexOf("-")
                        if (hyphenIndex > 0 && hyphenIndex < word.length - 1) {
                            lines.push(word.substring(0, hyphenIndex + 1))
                            currentLine = word.substring(hyphenIndex + 1)
                        } else {
                            const midPoint = Math.floor(word.length / 2)
                            lines.push(word.substring(0, midPoint))
                            currentLine = word.substring(midPoint)
                        }
                    } else {
                        lines.push(word)
                    }
                }
            }
        }

        if (currentLine) {
            lines.push(currentLine)
        }

        return lines
    }

    // Identity color for the wheel's chrome: a per-NPC (role-locked) wheel takes its role's accent
    // so it reads as "this one unit"; the global broadcast wheel stays neutral amber.
    private getAccent(): number {
        if (this.roleLocked) {
            const role = this.npcTypes[this.currentNPCIndex].name as CommandWheelRole
            return ROLE_ACCENT[role] ?? WHEEL_RIM_COLOR
        }
        return WHEEL_RIM_COLOR
    }

    // (Re)draw the glowing "glass" backdrop in the current accent color. Two faint accent rings fake
    // an outer glow (cheaper than a blur filter, which matters on low-end GPUs), then a dark
    // translucent disc with an accent rim, and an inner ring framing the donut hole. Called on every
    // display update so the accent tracks global (amber) vs per-NPC (role color).
    private drawBackground() {
        const accent = this.getAccent()
        const bg = this.backgroundCircle
        bg.clear()
        bg.beginFill(accent, 0.05)
        bg.drawCircle(0, 0, this.radius + 11)
        bg.endFill()
        bg.beginFill(accent, 0.09)
        bg.drawCircle(0, 0, this.radius + 5)
        bg.endFill()
        bg.lineStyle(2, accent, 0.9)
        bg.beginFill(this.backgroundColor, WHEEL_DISC_ALPHA)
        bg.drawCircle(0, 0, this.radius)
        bg.endFill()
        bg.lineStyle(1.5, accent, 0.35)
        bg.drawCircle(0, 0, this.radius * WHEEL_INNER_RATIO)
    }

    private setupWheel() {
        // The backdrop (glow + disc + rim + inner ring) is (re)drawn by drawBackground so it can pick
        // up the current accent color; slices + labels are built by updateDisplay. Both run on every
        // hover/selection change.
        this.backgroundCircle = new Graphics()
        this.addChild(this.backgroundCircle)
        this.drawBackground()

        // Make the wheel interactive
        this.interactive = true
        this.hitArea = new Circle(0, 0, this.radius)

        // Add mouse events
        this.on("pointermove", this.onPointerMove.bind(this))
        this.on("pointerdown", this.onPointerDown.bind(this))
        this.on("pointerout", this.onPointerOut.bind(this))

        // Prevent context menu from appearing
        this.on("rightclick", event => {
            event.stopPropagation()
        })
        this.on("contextmenu", event => {
            event.preventDefault()
            event.stopPropagation()
        })
    }

    private setupCenterDisplay() {
        // Remove existing center display elements
        if (this.centerBackground) {
            this.removeChild(this.centerBackground)
        }
        if (this.centerEmoji) {
            this.removeChild(this.centerEmoji)
        }
        if (this.switchGlyph) {
            this.removeChild(this.switchGlyph)
            this.switchGlyph = undefined
        }
        if (this.pageDots) {
            this.removeChild(this.pageDots)
            this.pageDots = undefined
        }

        const accent = this.getAccent()
        const hubRadius = this.radius * WHEEL_HUB_RATIO

        // Circular hub for the center emoji, sitting in the donut hole
        this.centerBackground = new Graphics()
        this.centerBackground.lineStyle(2, accent, 0.9)
        this.centerBackground.beginFill(this.backgroundColor, 0.95)
        this.centerBackground.drawCircle(0, 0, hubRadius)
        this.centerBackground.endFill()
        this.addChild(this.centerBackground)

        if (this.roleLocked) {
            // Per-NPC wheel: the hub is locked to one role — make it visibly inert (no switch
            // affordance, no hover glow, default cursor).
            this.centerBackground.interactive = false
        } else {
            // Global wheel: the hub cycles roles, so give it the full switch affordance — hover glow,
            // pointer cursor, a ↻ hint above the emoji, and a page dot per role below it.
            this.centerBackground.interactive = true
            this.centerBackground.cursor = "pointer"
            this.centerBackground.hitArea = new Circle(0, 0, hubRadius)
            this.centerBackground.on("pointerdown", event => {
                event.stopPropagation()
                this.switchNPC()
            })
            this.centerBackground.on("pointerover", () => {
                this.centerBackground.tint = accent
            })
            this.centerBackground.on("pointerout", () => {
                this.centerBackground.tint = 0xffffff
            })
            this.drawSwitchAffordance(accent, hubRadius)
        }

        // Create center emoji
        this.updateCenterEmoji()
    }

    // Draw the "this hub switches roles" cues for the global wheel: a ↻ glyph in the top gap ring and
    // one page dot per role in the bottom gap ring (the current role filled, the others outlined).
    // Both sit on the wheel's vertical seam (the inter-slice gap), so they don't overlap any slice.
    private drawSwitchAffordance(accent: number, hubRadius: number) {
        const innerRadius = this.radius * WHEEL_INNER_RATIO
        const gapMid = (hubRadius + innerRadius) / 2

        const glyph = new TextRenderer()
            .color(accent)
            .fontSize(Math.max(8, Math.round(hubRadius * 0.55)))
            .bold()
            .align("center")
            .render("↻")
        glyph.x = -glyph.width / 2
        glyph.y = -gapMid - glyph.height / 2
        glyph.alpha = 0.9
        this.switchGlyph = glyph
        this.addChild(glyph)

        const dots = new Graphics()
        const count = this.npcTypes.length
        const spacing = 6
        const startX = -((count - 1) * spacing) / 2
        for (let i = 0; i < count; i++) {
            const dx = startX + i * spacing
            if (i === this.currentNPCIndex) {
                dots.beginFill(accent, 1.0)
                dots.drawCircle(dx, gapMid, 1.8)
                dots.endFill()
            } else {
                dots.lineStyle(1, accent, 0.7)
                dots.drawCircle(dx, gapMid, 1.8)
            }
        }
        this.pageDots = dots
        this.addChild(dots)
    }

    private updateCenterEmoji() {
        // Remove existing center emoji if it exists
        if (this.centerEmoji) {
            this.removeChild(this.centerEmoji)
        }

        const currentNPC = this.npcTypes[this.currentNPCIndex]
        // Sized to sit inside the circular hub (radius = this.radius * WHEEL_HUB_RATIO).
        this.centerEmoji = new TextRenderer()
            .color(this.textColor)
            .fontSize(Math.round(this.radius * WHEEL_HUB_RATIO * 1.5))
            .bold()
            .align("center")
            .render(currentNPC.emoji)

        this.centerEmoji.x = -this.centerEmoji.width / 2
        this.centerEmoji.y = -this.centerEmoji.height / 2
        this.centerEmoji.alpha = 0.9
        this.addChild(this.centerEmoji)
    }

    private onPointerMove(event: any) {
        // Stop event propagation to prevent interfering with game mouse handling
        event.stopPropagation()

        const position = event.data.global
        const localPos = this.toLocal(position)
        const distance = Math.sqrt(localPos.x * localPos.x + localPos.y * localPos.y)

        // Over the center hub / donut hole → not hovering a slice
        const centerDistance = Math.sqrt(localPos.x * localPos.x + localPos.y * localPos.y)
        if (centerDistance <= this.radius * WHEEL_INNER_RATIO) {
            // Mouse is over center emoji, clear any hover state
            if (this.hoveredCommandIndex !== -1) {
                this.hoveredCommandIndex = -1
                this.updateDisplay()
            }
            return
        }

        if (distance <= this.radius) {
            const angle = Math.atan2(localPos.y, localPos.x) + Math.PI / 2
            const normalizedAngle = angle < 0 ? angle + Math.PI * 2 : angle

            const currentNPC = this.npcTypes[this.currentNPCIndex]
            const sliceAngle = (Math.PI * 2) / currentNPC.commands.length
            const commandIndex = Math.floor(normalizedAngle / sliceAngle)

            if (commandIndex !== this.hoveredCommandIndex && commandIndex < currentNPC.commands.length) {
                this.hoveredCommandIndex = commandIndex
                this.updateDisplay()
            }
        } else {
            if (this.hoveredCommandIndex !== -1) {
                this.hoveredCommandIndex = -1
                this.updateDisplay()
            }
        }
    }

    private onPointerDown(event: any) {
        // Stop event propagation to prevent triggering game attacks
        event.stopPropagation()

        const position = event.data.global
        const localPos = this.toLocal(position)
        const distance = Math.sqrt(localPos.x * localPos.x + localPos.y * localPos.y)

        // Ignore clicks in the donut hole / on the hub (the hub handles its own click).
        if (distance <= this.radius && distance >= this.radius * WHEEL_INNER_RATIO) {
            const angle = Math.atan2(localPos.y, localPos.x) + Math.PI / 2
            const normalizedAngle = angle < 0 ? angle + Math.PI * 2 : angle

            const currentNPC = this.npcTypes[this.currentNPCIndex]
            const sliceAngle = (Math.PI * 2) / currentNPC.commands.length
            const commandIndex = Math.floor(normalizedAngle / sliceAngle)

            if (commandIndex < currentNPC.commands.length) {
                this.activeCommandIndex = commandIndex
                this.updateDisplay()

                // Call the callback
                const command = currentNPC.commands[commandIndex]
                this.onCommandSelect?.(command.name, currentNPC.name)
            }
        }
    }

    private onPointerOut() {
        this.hoveredCommandIndex = -1
        this.updateDisplay()
    }

    private switchNPC() {
        // When bound to a single NPC the page is locked to that NPC's role.
        if (this.roleLocked) {
            return
        }
        this.currentNPCIndex = (this.currentNPCIndex + 1) % this.npcTypes.length
        this.activeCommandIndex = -1
        this.hoveredCommandIndex = -1
        this.updateCenterEmoji()
        this.updateDisplay()
    }

    private updateDisplay() {
        // Tear the previous slices/labels down completely (not just .clear()) so repeated hover
        // updates don't leave orphaned Graphics accumulating as children.
        this.commandSlices.forEach(slice => this.removeChild(slice))
        this.commandSlices = []
        this.commandTexts.forEach(text => this.removeChild(text))
        this.commandTexts = []

        // Refresh the backdrop so its accent tracks global (amber) vs per-NPC (role color).
        this.drawBackground()

        const currentNPC = this.npcTypes[this.currentNPCIndex]
        const sliceAngle = (Math.PI * 2) / currentNPC.commands.length
        const innerRadius = this.radius * WHEEL_INNER_RATIO
        const accent = this.getAccent()

        // Create new command slices and texts
        for (let i = 0; i < currentNPC.commands.length; i++) {
            const slice = new Graphics()
            this.commandSlices.push(slice)
            this.addChild(slice)

            // Create text container
            const textContainer = new Graphics()
            this.commandTexts.push(textContainer)
            this.addChild(textContainer)

            // Get the command text
            const commandText = currentNPC.commands[i].displayName

            // Calculate content dimensions for proper centering
            const maxWidth = this.radius * 0.72
            const wrappedLines = this.wrapText(commandText, maxWidth)
            const textLineHeight = 12

            // Calculate total content height for vertical centering
            const totalContentHeight = wrappedLines.length * textLineHeight

            // Calculate starting Y position to center the entire content block
            let currentY = -totalContentHeight / 2

            // Create text lines
            for (let lineIndex = 0; lineIndex < wrappedLines.length; lineIndex++) {
                const lineText = new TextRenderer()
                    .color(this.textColor)
                    .fontSize(11)
                    .bold()
                    .align("center")
                    .render(wrappedLines[lineIndex])

                textContainer.addChild(lineText)

                // Center each line horizontally
                lineText.x = -lineText.width / 2
                lineText.y = currentY + lineIndex * textLineHeight
            }

            // Position text container within the annulus band (between hole and rim)
            const angle = i * sliceAngle - Math.PI / 2 + sliceAngle / 2
            const textRadius = (innerRadius + this.radius) / 2

            // Nudge the top/bottom quadrants a touch so labels clear the center hub
            let verticalOffset = 0
            if (i === 0 || i === 3) {
                verticalOffset = -6
            } else if (i === 1 || i === 2) {
                verticalOffset = 6
            }

            textContainer.x = Math.cos(angle) * textRadius
            textContainer.y = Math.sin(angle) * textRadius + verticalOffset

            // State-driven fill + edge color (idle edge takes the wheel's accent)
            let fillColor = WHEEL_SLICE_IDLE_COLOR
            let fillAlpha = WHEEL_SLICE_IDLE_ALPHA
            let edgeColor = accent
            let edgeAlpha = 0.4
            if (i === this.activeCommandIndex) {
                fillColor = this.activeColor
                fillAlpha = 0.95
                edgeColor = this.activeColor
                edgeAlpha = 1.0
            } else if (i === this.hoveredCommandIndex) {
                fillColor = this.highlightColor
                fillAlpha = 0.9
                edgeColor = this.highlightColor
                edgeAlpha = 1.0
            }

            // Draw each slice as an annulus segment with a small gap on each side, so the commands
            // read as separated petals around the hub instead of a solid pie.
            const startAngle = i * sliceAngle - Math.PI / 2 + WHEEL_GAP_ANGLE
            const endAngle = (i + 1) * sliceAngle - Math.PI / 2 - WHEEL_GAP_ANGLE

            slice.lineStyle(2, edgeColor, edgeAlpha)
            slice.beginFill(fillColor, fillAlpha)
            slice.moveTo(Math.cos(startAngle) * innerRadius, Math.sin(startAngle) * innerRadius)
            slice.lineTo(Math.cos(startAngle) * this.radius, Math.sin(startAngle) * this.radius)
            slice.arc(0, 0, this.radius, startAngle, endAngle)
            slice.lineTo(Math.cos(endAngle) * innerRadius, Math.sin(endAngle) * innerRadius)
            slice.arc(0, 0, innerRadius, endAngle, startAngle, true)
            slice.closePath()
            slice.endFill()
        }

        // Ensure center display is always on top
        this.setupCenterDisplay()
    }

    public getCurrentNPC(): string {
        return this.npcTypes[this.currentNPCIndex].name
    }

    public getActiveCommand(): string | null {
        if (this.activeCommandIndex >= 0) {
            return this.npcTypes[this.currentNPCIndex].commands[this.activeCommandIndex].name
        }
        return null
    }

    public clearSelection() {
        this.activeCommandIndex = -1
        this.hoveredCommandIndex = -1
        this.updateDisplay()
    }

    // Bind the wheel to a single NPC and lock it to that NPC's role page. Used when the wheel is
    // opened from an entity's command button (server tells us which role).
    public bindToEntity(targetEntityId: string, role: CommandWheelRole) {
        this.targetEntityId = targetEntityId
        this.roleLocked = true
        const roleIndex = this.npcTypes.findIndex(npc => npc.name === role)
        this.currentNPCIndex = roleIndex >= 0 ? roleIndex : this.currentNPCIndex
        this.activeCommandIndex = -1
        this.hoveredCommandIndex = -1
        this.updateCenterEmoji()
        this.updateDisplay()
    }

    // Return to the global (broadcast) wheel: no bound entity, role switching re-enabled.
    public clearTarget() {
        this.targetEntityId = undefined
        this.roleLocked = false
    }

    public getTargetEntityId(): string | undefined {
        return this.targetEntityId
    }

    public setVisible(visible: boolean) {
        if (visible) {
            this.fadeIn()
        } else {
            this.fadeOut()
        }
    }

    // Whether the wheel is logically open — true from the moment a fade-in starts until a fade-out
    // starts. Use this (not `.visible`) for input gating so releasing the wheel frees clicks
    // immediately rather than after the dismiss animation finishes.
    public isOpen(): boolean {
        return this.opened
    }

    // Reveal the wheel by fading it in. Interrupts an in-progress dismiss so a quick re-open picks
    // up from the current alpha instead of snapping.
    public fadeIn() {
        this.opened = true

        if (this.fadeOutFader) {
            this.fadeOutFader.stop(true)
            this.fadeOutFader = null
        }
        if (this.fadeInFader) {
            return
        }

        // Start from fully transparent only when coming from a hidden state; if we're interrupting a
        // fade-out, continue from wherever the alpha currently sits.
        if (!this.visible) {
            this.alpha = 0
        }
        this.visible = true

        if (this.alpha >= 1) {
            this.alpha = 1
            return
        }

        this.fadeInFader = new Fader(this, {
            direction: "in",
            minAlpha: 1.0,
            fadeInterval: WHEEL_FADE_INTERVAL_MS,
            fadeAlphaDecrement: WHEEL_FADE_STEP,
            releaseCallback: () => {
                this.fadeInFader = null
                this.alpha = 1
            },
        })
        this.fadeInFader.update()
    }

    // Dismiss the wheel by fading it out, hiding it once transparent. Interrupts an in-progress
    // reveal so a quick close starts from the current alpha.
    public fadeOut() {
        this.opened = false

        if (this.fadeInFader) {
            this.fadeInFader.stop(true)
            this.fadeInFader = null
        }
        if (this.fadeOutFader || !this.visible) {
            return
        }

        this.fadeOutFader = new Fader(this, {
            direction: "out",
            minAlpha: 0.0,
            fadeInterval: WHEEL_FADE_INTERVAL_MS,
            fadeAlphaDecrement: WHEEL_FADE_STEP,
            releaseCallback: () => {
                this.fadeOutFader = null
                this.visible = false
                this.alpha = 1.0
            },
        })
        this.fadeOutFader.update()
    }
}
